import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { useVoicesToday } from "../../../Query/useVoicesOverview";
import Loading from "../../Common/Loading";
import VoicesCard from "../Common/VoicesCard";
import VoicesPostRows from "../Posts/VoicesPostRows";

const VoicesToday: React.FC = () => {
  const { t } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const navigate = useNavigate();
  const { data: today, isLoading } = useVoicesToday();

  if (isLoading || !today) {
    return (
      <Loading backgroundColor={color.gray50} color1={workspace.accent} color2={color.gray900} />
    );
  }

  const sections = [
    { key: "waiting", posts: today.waiting_for_me },
    { key: "rejected", posts: today.rejected },
    { key: "failed", posts: today.failed },
  ].filter((section) => section.posts.length > 0);

  if (!sections.length && !today.new_topics.length) {
    return (
      <VoicesCard title={t("today_allDoneTitle")}>
        <Typography variant="body2" sx={{ color: color.gray600 }}>
          {t("today_allDone")}
        </Typography>
      </VoicesCard>
    );
  }

  return (
    <>
      {sections.map((section) => (
        <VoicesCard
          key={section.key}
          title={t(`today_${section.key}`, { count: section.posts.length })}
          subtitle={t(`today_${section.key}Hint`)}
        >
          <VoicesPostRows posts={section.posts} emptyText="" showCampaign />
        </VoicesCard>
      ))}

      {today.new_topics.length > 0 && (
        <VoicesCard title={t("today_newTopics")} subtitle={t("today_newTopicsHint")}>
          <Stack spacing={1}>
            {today.new_topics.map((campaign) => (
              <Box
                key={campaign.campaign_id}
                onClick={() => navigate(`/voices/campaigns/${campaign.campaign_id}`)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${color.gray200}`,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: color.orange50 },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {campaign.name}
                </Typography>
                <Chip
                  size="small"
                  label={t("today_topicCount", { count: campaign.count })}
                  sx={{
                    backgroundColor: color.orange50,
                    border: `1px solid ${color.orange200}`,
                    color: color.gray900,
                  }}
                />
              </Box>
            ))}
          </Stack>
        </VoicesCard>
      )}
    </>
  );
};

export default VoicesToday;
